import { Fragment, useState, useEffect, useCallback, useMemo } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { PageModal } from '@/components/modals/PageModal';
import { NPSDClientScreenWrapper } from '@/components/npsd/NPSDClientScreen';
import { ActiveFormSummariesSection } from '@/components/tourstop/ActiveFormSummariesSection';
import { api } from '@/services/api-class';
import * as apiPromise from '@/services/api';
import { b64toBlob } from '@/utils/blob';
import { saveAs } from 'file-saver';
import { getOfficerLabel } from '@/utils/legacy-utils';
import { hasImageExtension } from '@/utils/dom';
import { App } from '@/services/app-compat';
import moment from 'moment-timezone';

interface TourStopCompletionScreenProps {
  completionId?: number | string;
}

export function TourStopCompletionScreen({ completionId }: TourStopCompletionScreenProps) {
  const { theme } = useTheme();
  const [detail, setDetail] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Client modal state
  const [showClientModal, setShowClientModal] = useState(false);
  const [clientModalId, setClientModalId] = useState<number | null>(null);

  const officerLabel = useMemo(() => {
    return typeof getOfficerLabel === 'function' ? getOfficerLabel() : 'Officer';
  }, []);

  // Load completion detail
  useEffect(() => {
    if (!completionId) {
      setError('No completion ID provided');
      setIsLoading(false);
      return;
    }

    const id = typeof completionId === 'string' ? parseInt(completionId) : completionId;
    setIsLoading(true);
    setError(null);

    api.getTourStopCompletionDetail(id, (result: any) => {
      if (result && result.id) {
        setDetail(result);
      } else if (result?.error) {
        setError(result.error);
      } else {
        setError('Completion not found');
      }
      setIsLoading(false);
    });
  }, [completionId]);

  const formatDateTime = useCallback((dateStr: string): string => {
    if (!dateStr) return '\u2014';
    const m = moment.utc(dateStr);
    if (!m.isValid()) return dateStr;
    return m.local().format('MM/DD/YYYY hh:mm A');
  }, []);

  const handleDownloadPdf = useCallback(() => {
    if (!detail?.id) return;
    const timeZoneId = moment.tz.guess();
    App.showPopupNotification('Preparing PDF for download. Please wait.', 'success');

    apiPromise.getTourStopCompletionPdf(detail.id, timeZoneId)
      .then((result: any) => {
        if (result?.pdfBase64) {
          const blob = b64toBlob(result.pdfBase64, 'application/pdf');
          saveAs(blob, result.pdfFilename || 'Tour Stop Completion.pdf');
        } else {
          App.showPopupNotification('Failed to generate PDF.', 'error', 30);
        }
      })
      .catch(() => {
        App.showPopupNotification('Failed to generate PDF.', 'error', 30);
      });
  }, [detail]);

  const openClientModal = useCallback(() => {
    if (detail?.clientId) {
      setClientModalId(detail.clientId);
      setShowClientModal(true);
    }
  }, [detail]);

  const closeClientModal = useCallback(() => {
    setShowClientModal(false);
    setClientModalId(null);
  }, []);

  const openAttachmentUrl = useCallback((url: string) => {
    if (url) window.open(url, '_blank');
  }, []);

  const renderAttachmentGrid = (attachmentList: any[], resizedImageList: any[] | undefined) => (
    <div className="tsc-attachments-grid">
      {attachmentList.map((attachment: any, idx: number) => {
        const isImage = hasImageExtension(attachment.filename);
        const resized = isImage && resizedImageList
          ? resizedImageList.find((r: any) => r.id === attachment.id)
          : null;
        return (
          <div key={idx} className="tsc-attachment-item" onClick={() => openAttachmentUrl(attachment.signedURL)}>
            {isImage && resized?.signedURL ? (
              <img src={resized.signedURL} className="tsc-attachment-image" alt={attachment.filename} />
            ) : (
              <div className="tsc-attachment-file">
                <i className={`fas ${isImage ? 'fa-image' : 'fa-file'}`}></i>
                <span className="tsc-attachment-filename">{attachment.filename}</span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  if (isLoading) {
    return (
      <div className={`tour-stop-completion-screen ${theme}`}>
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <div>Loading completion details...</div>
        </div>
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div className={`tour-stop-completion-screen ${theme}`}>
        <div className="tsc-error">{error || 'Completion not found'}</div>
      </div>
    );
  }

  return (
    <div className={`tour-stop-completion-screen ${theme}`}>
      {/* Page Header */}
      <div className="tsc-page-header">
        <div className="tsc-page-header-content">
          <h1 className="tsc-page-title">Tour Stop Completion</h1>
          <span className="tsc-page-badge">{detail.tourStopTitle}</span>
        </div>
        <div className="tsc-page-actions">
          <button type="button" className="tsc-btn tsc-btn-pdf" onClick={handleDownloadPdf}>
            <i className="fas fa-file-pdf"></i> Download PDF
          </button>
        </div>
      </div>

      {/* Completion Details */}
      <div className="tsc-panel">
        <div className="tsc-panel-header">
          <i className="fas fa-clipboard-check"></i> Completion Details
        </div>
        <div className="tsc-panel-body">
          <div className="tsc-detail-grid">
            <div className="tsc-detail-item">
              <span className="tsc-detail-label">{officerLabel}</span>
              <span className="tsc-detail-value">
                {detail.officerName}
                {detail.officerCallSign && ` (${detail.officerCallSign})`}
              </span>
            </div>
            <div className="tsc-detail-item">
              <span className="tsc-detail-label">Completed At</span>
              <span className="tsc-detail-value">{formatDateTime(detail.completedAt)}</span>
            </div>
            <div className="tsc-detail-item">
              <span className="tsc-detail-label">Tour</span>
              <span className="tsc-detail-value">{detail.tourTitle}</span>
            </div>
            <div className="tsc-detail-item">
              <span className="tsc-detail-label">Location</span>
              <span className="tsc-detail-value">
                {detail.latitude && detail.longitude
                  ? `${detail.latitude}, ${detail.longitude}`
                  : '\u2014'}
              </span>
            </div>
            {detail.tourExemption && (
              <div className="tsc-detail-item full-width">
                <span className="tsc-detail-label">Exemption</span>
                <span className="tsc-detail-value">{detail.tourExemption.name}</span>
              </div>
            )}
            {detail.officerComments && (
              <div className="tsc-detail-item full-width">
                <span className="tsc-detail-label">{officerLabel} Comments</span>
                <span className="tsc-detail-value" dangerouslySetInnerHTML={{ __html: detail.officerComments }} />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Attachments */}
      {detail.attachmentList && detail.attachmentList.length > 0 && (
        <div className="tsc-panel">
          <div className="tsc-panel-header">
            <i className="fas fa-paperclip"></i> Attachments
          </div>
          <div className="tsc-panel-body">
            {renderAttachmentGrid(detail.attachmentList, detail.resizedImageList)}
          </div>
        </div>
      )}

      {/* Tour Stop Info */}
      <div className="tsc-panel">
        <div className="tsc-panel-header">
          <i className="fas fa-map-marker-alt"></i> Tour Stop Information
        </div>
        <div className="tsc-panel-body">
          <div className="tsc-detail-grid">
            <div className="tsc-detail-item">
              <span className="tsc-detail-label">Name</span>
              <span className="tsc-detail-value">{detail.tourStopTitle}</span>
            </div>
            <div className="tsc-detail-item">
              <span className="tsc-detail-label">Location</span>
              <span className="tsc-detail-value">{detail.tourStopLocation || '\u2014'}</span>
            </div>
            {detail.tourStopInstructions && (
              <div className="tsc-detail-item full-width">
                <span className="tsc-detail-label">Instructions</span>
                <span className="tsc-detail-value" dangerouslySetInnerHTML={{ __html: detail.tourStopInstructions }} />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tasks */}
      <div className="tsc-panel">
        <div className="tsc-panel-header">
          <i className="fas fa-tasks"></i> Tasks
        </div>
        <div className="tsc-panel-body">
          {detail.tasks && detail.tasks.length > 0 ? (
            <div className="tsc-table-wrap">
              <table className="tsc-table">
                <thead>
                  <tr>
                    <th>Task</th>
                    <th>Description</th>
                    <th>Status</th>
                    <th>Exception</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.tasks.map((task: any, idx: number) => (
                    <tr key={idx}>
                      <td>{task.name}</td>
                      <td>{task.description || '\u2014'}</td>
                      <td>
                        {task.completedAt ? (
                          task.isTaskCompletionExemption
                            ? <span className="tsc-badge tsc-badge-exemption">Exemption</span>
                            : <span className="tsc-badge tsc-badge-completed">Completed</span>
                        ) : (
                          <span className="tsc-badge tsc-badge-not-completed">Not Completed</span>
                        )}
                      </td>
                      <td>{task.tourExemption?.name || '\u2014'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="tsc-empty">No tasks assigned.</div>
          )}
        </div>
      </div>

      {/* Issues */}
      {detail.issues && detail.issues.length > 0 && (
        <div className="tsc-panel">
          <div className="tsc-panel-header">
            <i className="fas fa-exclamation-triangle"></i> Issues
          </div>
          <div className="tsc-panel-body">
            <div className="tsc-table-wrap">
              <table className="tsc-table">
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Description</th>
                    <th>Raised By</th>
                    <th>Raised At</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.issues.map((issue: any, idx: number) => {
                    const hasPhotos = issue.attachmentList && issue.attachmentList.length > 0;
                    return (
                    <Fragment key={idx}>
                    <tr className={hasPhotos ? 'tsc-issue-row-with-photos' : undefined}>
                      <td>{issue.title}</td>
                      <td>{issue.description}</td>
                      <td>{issue.raisedByName}</td>
                      <td>{formatDateTime(issue.raisedAt)}</td>
                      <td>
                        {issue.resolvedAt ? (
                          <span className="tsc-badge tsc-badge-resolved">
                            Resolved {formatDateTime(issue.resolvedAt)}
                            {issue.resolvedByName && ` by ${issue.resolvedByName}`}
                          </span>
                        ) : (
                          <span className="tsc-badge tsc-badge-unresolved">Unresolved</span>
                        )}
                      </td>
                    </tr>
                    {hasPhotos && (
                      <tr className="tsc-issue-photos-row">
                        <td colSpan={5}>
                          {renderAttachmentGrid(issue.attachmentList, issue.resizedImageList)}
                        </td>
                      </tr>
                    )}
                    </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Client Contact Info */}
      <div className="tsc-panel">
        <div className="tsc-panel-header">
          <i className="fas fa-address-card"></i> Client Contact Information
        </div>
        <div className="tsc-panel-body">
          <div className="tsc-detail-grid">
            <div className="tsc-detail-item">
              <span className="tsc-detail-label">Client</span>
              <span className="tsc-detail-value">
                {detail.clientId ? (
                  <a role="button" onClick={openClientModal}>{detail.clientName}</a>
                ) : (
                  detail.clientName
                )}
              </span>
            </div>
            {detail.clientAddress && (
              <div className="tsc-detail-item">
                <span className="tsc-detail-label">Address</span>
                <span className="tsc-detail-value">{detail.clientAddress}</span>
              </div>
            )}
            {detail.clientWebsite && (
              <div className="tsc-detail-item">
                <span className="tsc-detail-label">Website</span>
                <span className="tsc-detail-value">{detail.clientWebsite}</span>
              </div>
            )}
          </div>

          {/* Phone Numbers */}
          {detail.clientPhoneNumbers && detail.clientPhoneNumbers.length > 0 && (
            <div className="tsc-contact-section">
              <div className="tsc-contact-section-label">Phone Numbers</div>
              <div className="tsc-contact-list">
                {detail.clientPhoneNumbers.map((phone: any, idx: number) => (
                  <div key={idx} className="tsc-contact-item">
                    <span className="tsc-contact-item-type">{phone.phoneType || 'Phone'}</span>
                    <span>{phone.phoneNumber}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Emails */}
          {detail.clientEmails && detail.clientEmails.length > 0 && (
            <div className="tsc-contact-section">
              <div className="tsc-contact-section-label">Email Addresses</div>
              <div className="tsc-contact-list">
                {detail.clientEmails.map((email: any, idx: number) => (
                  <div key={idx} className="tsc-contact-item">
                    <span className="tsc-contact-item-type">{email.emailType || 'Email'}</span>
                    <span>{email.email}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Gate Codes */}
          {detail.clientGateCodes && detail.clientGateCodes.length > 0 && (
            <div className="tsc-contact-section">
              <div className="tsc-contact-section-label">Gate Codes</div>
              <div className="tsc-tags">
                {detail.clientGateCodes.map((code: any, idx: number) => (
                  <span key={idx} className="tsc-tag tsc-tag-info">
                    {typeof code === 'string' ? code : code.code || code.name || ''}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Warnings */}
          {detail.clientWarnings && detail.clientWarnings.length > 0 && (
            <div className="tsc-contact-section">
              <div className="tsc-contact-section-label">Warnings</div>
              <div className="tsc-tags">
                {detail.clientWarnings.map((warning: any, idx: number) => (
                  <span key={idx} className="tsc-tag tsc-tag-warning">
                    {typeof warning === 'string' ? warning : warning.warning || ''}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Custom Forms projection (Phase D backend; Phase G frontend). */}
      <ActiveFormSummariesSection summaries={detail.activeFormSummaries} />

      {/* Client PageModal */}
      <PageModal
        open={showClientModal}
        title="Client"
        modalId="tourStopCompletionClientModal"
        onClose={closeClientModal}
      >
        {showClientModal && clientModalId && (
          <NPSDClientScreenWrapper
            clientId={clientModalId}
            isCaseReportMode={true}
          />
        )}
      </PageModal>
    </div>
  );
}

// Wrapper that extracts the completionId from route params
export function TourStopCompletionScreenWrapper({ completionId }: { completionId?: number | string }) {
  return <TourStopCompletionScreen completionId={completionId} />;
}
